import { useCallback, useState } from 'react';
import { getChipDom } from '@/utils/questions/dnd/dndNodes';
import { containerOf, StringPlacement } from '@/utils/questions/dnd/resolvePlacement';
import { BANK_ID } from '@/utils/questions/dnd/types';

export type FlipOrigin = { rect: DOMRect; fromSlot: boolean };

type CaptureMovesArgs = {
  previous: StringPlacement;
  next: StringPlacement;
  exclude?: string;
};

export function usePlacementFlip() {
  const [flipFrom, setFlipFrom] = useState<Record<string, FlipOrigin>>({});

  const clearFlip = useCallback((id: string) => {
    setFlipFrom(current => {
      if (!(id in current)) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  }, []);

  const captureMoves = useCallback(
    ({ previous, next, exclude }: CaptureMovesArgs) => {
      const ids = new Set([
        ...Object.values(previous).flat(),
        ...Object.values(next).flat(),
      ]);

      const captured: Record<string, FlipOrigin> = {};
      ids.forEach(id => {
        if (id === exclude) return;

        const before = containerOf(previous, id);
        const after = containerOf(next, id);
        if (!before || !after || before === after) return;

        const node = getChipDom(id);
        if (!node) return;

        const previousTransform = node.style.transform;
        node.style.transform = 'none';
        const rect = node.getBoundingClientRect();
        node.style.transform = previousTransform;

        captured[id] = { rect, fromSlot: before !== BANK_ID };
      });

      if (Object.keys(captured).length) {
        setFlipFrom(current => ({ ...current, ...captured }));
      }
    },
    [],
  );

  return { flipFrom, clearFlip, captureMoves };
}
